import ParagraphLayoutBehavior from "./ParagraphLayoutBehavior";
import ParagraphBoundaryPreference from "./ParagraphBoundaryPreference";

const BREAK_STRENGTH = Object.freeze({
    HARD: "hard",
    SOFT: "soft",
});

function normalizeBreakStrength(strength){
    if(strength === BREAK_STRENGTH.HARD || strength === BREAK_STRENGTH.SOFT){
        return strength;
    }
    return null;
}

function createBreakDecision(shouldBreak, reason, strength){
    return {
        shouldBreak: !!shouldBreak,
        reason: reason || null,
        strength: shouldBreak ? normalizeBreakStrength(strength) || BREAK_STRENGTH.HARD : null,
    };
}

function normalizeBreakDecision(decision){
    if(!decision){
        return createBreakDecision(false, null, null);
    }
    return createBreakDecision(decision.shouldBreak, decision.reason, decision.strength);
}

function normalizeRuleArray(rules){
    if(!Array.isArray(rules)){
        return [];
    }
    return rules.filter((rule)=>{
        return typeof rule === "function";
    });
}

function mergeBehaviorRules(defaultRules, customRules){
    return defaultRules.concat(normalizeRuleArray(customRules));
}

function mergeDecisionRules(defaultRules, customRules){
    return normalizeRuleArray(customRules).concat(defaultRules);
}

function matchesNodeType(nodeTypeMatcher, nodeType, sequencerNode){
    if(typeof nodeTypeMatcher === "function"){
        return !!nodeTypeMatcher(nodeType, sequencerNode);
    }
    if(Array.isArray(nodeTypeMatcher)){
        return nodeTypeMatcher.indexOf(nodeType) > -1;
    }
    return nodeType === nodeTypeMatcher;
}

function resolveNodeProvidedBehavior(context){
    return context && context.sequencerNode && context.sequencerNode.getParagraphLayoutBehavior
        ? context.sequencerNode.getParagraphLayoutBehavior()
        : null;
}

function breakAfterLongPause(context){
    if(
        !context
        || !context.paragraphBehavior
        || !Number.isFinite(context.longPauseDurationInMillisecondsToForceBreak)
        || context.longPauseDurationInMillisecondsToForceBreak <= 0
        || !context.isPauseSequencerNode
        || !context.isPauseSequencerNode(context.sequencerNode)
        || !context.getNodeDurationInMilliseconds
        || context.getNodeDurationInMilliseconds(context.sequencerNode) < context.longPauseDurationInMillisecondsToForceBreak
    ){
        return null;
    }
    // Preserve any annotation an earlier behavior rule set — we only want to
    // *ensure* the break fires with a reason/strength, not clobber a stronger
    // (e.g. HARD) classification from a wrap-node rule.
    const paragraphBehavior = context.paragraphBehavior;
    return {
        breakAfter: true,
        breakAfterReason: paragraphBehavior.breakAfterReason || "long-pause",
        breakAfterStrength: paragraphBehavior.breakAfterStrength || BREAK_STRENGTH.SOFT,
    };
}

function nextAffinityDeferRule(context){
    const paragraphBehavior = context ? context.paragraphBehavior : null;
    if(
        !paragraphBehavior
        || paragraphBehavior.affinity !== ParagraphLayoutBehavior.AFFINITY.NEXT
        || paragraphBehavior.breakAfter
        || paragraphBehavior.isolateInOwnParagraph
    ){
        return null;
    }
    if(
        paragraphBehavior.speakerInfoOverride
        || paragraphBehavior.paragraphKind
        || (context && context.getExplicitSpeakerInfo && context.getExplicitSpeakerInfo(context.sequencerNode))
    ){
        return {
            canDefer: false,
        };
    }
    return {
        canDefer: true,
    };
}

function breakWhenNoCurrentParagraphRule(context){
    if(!context || !context.currentParagraph){
        return createBreakDecision(true, "missing-current-paragraph", BREAK_STRENGTH.HARD);
    }
    return null;
}

function breakWhenPendingBoundaryRule(context){
    if(context && context.pendingBreakDecision && context.pendingBreakDecision.shouldBreak){
        return normalizeBreakDecision(context.pendingBreakDecision);
    }
    if(context && context.shouldBreakBeforeNextNode){
        return createBreakDecision(true, "pending-boundary", BREAK_STRENGTH.HARD);
    }
    return null;
}

function breakWhenNodeDemandsBoundaryRule(context){
    const paragraphBehavior = context ? context.paragraphBehavior : null;
    if(paragraphBehavior && (paragraphBehavior.breakBefore || paragraphBehavior.isolateInOwnParagraph)){
        return createBreakDecision(
            true,
            paragraphBehavior.isolateInOwnParagraph
                ? paragraphBehavior.breakBeforeReason || "isolated-node"
                : paragraphBehavior.breakBeforeReason || "break-before-node",
            paragraphBehavior.isolateInOwnParagraph
                ? paragraphBehavior.breakBeforeStrength || paragraphBehavior.breakAfterStrength || BREAK_STRENGTH.HARD
                : paragraphBehavior.breakBeforeStrength || BREAK_STRENGTH.HARD,
        );
    }
    return null;
}

function breakWhenParagraphCannotAcceptNodesRule(context){
    const compatibilityDecision = context && context.getParagraphCompatibilityDecision
        ? context.getParagraphCompatibilityDecision(context.nextParagraphNodes, context.currentParagraph)
        : null;
    if(compatibilityDecision && compatibilityDecision.canAdd === false){
        return createBreakDecision(
            true,
            compatibilityDecision.reason || "paragraph-compatibility",
            compatibilityDecision.strength || BREAK_STRENGTH.HARD,
        );
    }
    if(
        context
        && context.currentParagraph
        && context.canNodesBeAddedToParagraph
        && !context.canNodesBeAddedToParagraph(context.nextParagraphNodes, context.currentParagraph)
    ){
        // Boolean fallback cannot distinguish soft (word/sentence limit) from
        // hard (speaker/kind) incompatibility. Default to SOFT so user
        // paragraph-boundary preferences can still override if needed. Callers
        // wanting HARD must provide the structured `getParagraphCompatibilityDecision`.
        return createBreakDecision(true, "paragraph-compatibility", BREAK_STRENGTH.SOFT);
    }
    return null;
}

const BASE_RULES = Object.freeze({
    behaviorRules: Object.freeze([]),
    deferRules: Object.freeze([]),
    breakRules: Object.freeze([]),
});

function createRuleToExtendBehaviorForNodeType(nodeTypeMatcher, behaviorOverrides){
    return function(context){
        const nodeType = context && context.getNodeType ? context.getNodeType(context.sequencerNode) : null;
        if(!matchesNodeType(nodeTypeMatcher, nodeType, context ? context.sequencerNode : null)){
            return null;
        }
        return behaviorOverrides || null;
    };
}

function resolveNodeBehaviorWithRules(context, rules){
    let paragraphBehavior = ParagraphLayoutBehavior.create();
    const baseContext = Object.assign({}, context);
    for(let i = 0; i < rules.length; i++){
        const behaviorOverrides = rules[i](Object.assign({}, baseContext, {
            paragraphBehavior: paragraphBehavior,
        }));
        if(behaviorOverrides){
            paragraphBehavior = ParagraphLayoutBehavior.extend(paragraphBehavior, behaviorOverrides);
        }
    }
    return paragraphBehavior;
}

function resolveDeferDecisionWithRules(context, rules){
    for(let i = 0; i < rules.length; i++){
        const decision = rules[i](context);
        if(decision){
            return Object.assign({
                canDefer: false,
                reason: null,
            }, decision);
        }
    }
    return {
        canDefer: false,
        reason: null,
    };
}

function resolveBreakDecisionWithRules(context, rules){
    for(let i = 0; i < rules.length; i++){
        const decision = rules[i](context);
        if(decision && decision.shouldBreak){
            return normalizeBreakDecision(decision);
        }
    }
    return createBreakDecision(false, null, null);
}

function getParagraphBoundaryPreferenceForNode(context){
    // Try each source in priority order; fall through when a source yields
    // null/undefined so that a node with only a stored Backbone attribute is
    // still respected.
    if(context && context.getParagraphBoundaryPreference){
        const fromContextHelper = ParagraphBoundaryPreference.normalize(
            context.getParagraphBoundaryPreference(context.sequencerNode),
        );
        if(fromContextHelper){
            return fromContextHelper;
        }
    }
    if(context && context.sequencerNode && context.sequencerNode.getParagraphBoundaryPreference){
        const fromNodeMethod = ParagraphBoundaryPreference.normalize(
            context.sequencerNode.getParagraphBoundaryPreference(),
        );
        if(fromNodeMethod){
            return fromNodeMethod;
        }
    }
    if(context && context.sequencerNode && typeof context.sequencerNode.get === "function"){
        return ParagraphBoundaryPreference.normalize(context.sequencerNode.get("paragraphBoundaryPreference"));
    }
    return null;
}

function applyParagraphBoundaryPreferenceToBreakDecision(decision, context){
    const normalizedDecision = normalizeBreakDecision(decision);
    const paragraphBoundaryPreference = getParagraphBoundaryPreferenceForNode(context);
    if(ParagraphBoundaryPreference && ParagraphBoundaryPreference.isBreakBefore && ParagraphBoundaryPreference.isBreakBefore(paragraphBoundaryPreference)){
        if(normalizedDecision.shouldBreak && normalizedDecision.strength === BREAK_STRENGTH.HARD){
            return normalizedDecision;
        }
        return createBreakDecision(true, "manual-break-before", BREAK_STRENGTH.SOFT);
    }
    if(
        ParagraphBoundaryPreference
        && ParagraphBoundaryPreference.isJoinWithPrevious
        && ParagraphBoundaryPreference.isJoinWithPrevious(paragraphBoundaryPreference)
    ){
        if(normalizedDecision.shouldBreak && normalizedDecision.strength !== BREAK_STRENGTH.HARD){
            return createBreakDecision(false, "manual-join-with-previous", null);
        }
        return normalizedDecision;
    }
    return normalizedDecision;
}

function createParagraphBoundaryPolicy(customRules){
    const mergedRules = {
        behaviorRules: mergeBehaviorRules(BASE_RULES.behaviorRules, customRules && customRules.behaviorRules),
        deferRules: mergeDecisionRules(BASE_RULES.deferRules, customRules && customRules.deferRules),
        breakRules: mergeDecisionRules(BASE_RULES.breakRules, customRules && customRules.breakRules),
    };

    return Object.freeze({
        rules: mergedRules,

        resolveNodeBehavior: function(context){
            return resolveNodeBehaviorWithRules(context, mergedRules.behaviorRules);
        },

        getDeferDecision: function(context){
            return resolveDeferDecisionWithRules(context, mergedRules.deferRules);
        },

        canDeferNodeToFollowingParagraph: function(context){
            return this.getDeferDecision(context).canDefer;
        },

        getParagraphBreakDecision: function(context){
            return applyParagraphBoundaryPreferenceToBreakDecision(
                resolveBreakDecisionWithRules(context, mergedRules.breakRules),
                context,
            );
        },

        shouldStartNewParagraph: function(context){
            return this.getParagraphBreakDecision(context).shouldBreak;
        },
    });
}

const EmptyParagraphBoundaryPolicy = createParagraphBoundaryPolicy();

const ParagraphBoundaryPolicy = Object.freeze(Object.assign({}, EmptyParagraphBoundaryPolicy, {
    BASE_RULES: BASE_RULES,
    BREAK_STRENGTH: BREAK_STRENGTH,
    create: createParagraphBoundaryPolicy,
    ruleFactories: Object.freeze({
        resolveNodeProvidedBehavior: resolveNodeProvidedBehavior,
        breakAfterLongPause: breakAfterLongPause,
        deferNextAffinityNodes: nextAffinityDeferRule,
        breakWhenMissingCurrentParagraph: breakWhenNoCurrentParagraphRule,
        breakWhenPendingBoundary: breakWhenPendingBoundaryRule,
        breakWhenNodeDemandsBoundary: breakWhenNodeDemandsBoundaryRule,
        breakWhenParagraphCannotAcceptNodes: breakWhenParagraphCannotAcceptNodesRule,
        extendBehaviorForNodeType: createRuleToExtendBehaviorForNodeType,
    }),
}));

export default ParagraphBoundaryPolicy;
